import React, { useEffect, useState, useRef } from 'react';
import { Box, Typography, Button } from '@mui/material';
import PositionTable from '../Positions/ViewPositionsPage/PositionTable';
import { StoneXNumber } from '../../../StoneXMui';
import { BudgetModel, ContractDate, DeliveryModel, PositionModel, PositionType, Unit as CommodityUnit } from '../../../../Generated/Commodity-Management-Api';
import { Unit } from '../../../../Generated/Mark-It-View-Api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { ValueType, NameType } from 'recharts/types/component/DefaultTooltipContent';
import { LegendType } from 'recharts';
import { splitFuturesAcrossBudgetMonths } from '../../../../Helpers';
import { FiscalMonth } from '../../../../Helpers/FiscalYearHelper';
import { numberToShortMonth } from '../../../../Helpers/MonthHelper';
import { CommodityContractMonthMappingModel } from '../../../../Apis/ContractMonthMappingApi';
import useFloatingHorizontalScrollbar from '../../../../Hooks/useFloatingHorizontalScrollbar';

// The card faces are a fixed 400px (.position-card-summary in Dashboard.css), three to a
// row. The row cannot be narrower than that, so below it the cards scroll sideways
// rather than overlapping or being cut off.
const CARD_WIDTH = 400;
const CARDS_PER_ROW = 3;
const CARD_GAP = 16;
const CARDS_MIN_WIDTH = CARD_WIDTH * CARDS_PER_ROW + CARD_GAP * (CARDS_PER_ROW - 1);

interface SummaryProps {
    futures: PositionModel[];
    /**
     * Commodity settings' budget month -> contract month mapping, used to spread
     * contract-month futures volume over the budget months it covers. Omitted or empty
     * leaves each position on its own contract month.
     */
    contractMonthMappings?: CommodityContractMonthMappingModel[];
    positionsByAllocation: PositionModel[];
    forecast: BudgetModel[];
    /**
     * Every month of the selected fiscal year. A card is shown for each one, with
     * dashes where there is no budget. Omitted, only budgeted months get a card.
     */
    months?: FiscalMonth[];
    /** The commodity the cards are for; used to match hedges on months without a budget. */
    commodityId?: number;
    deliveries?: DeliveryModel[];
    displayUnit?: CommodityUnit;
}

/** A budget-shaped stand-in for a month that has no budget, so it still gets a card. */
function emptyBudgetForMonth({ year, month }: FiscalMonth, commodityId: number | undefined): BudgetModel {
    return {
        budgetDate: { year, month, label: `${numberToShortMonth(month)} ${String(year).slice(-2)}` },
        commodity: { commodityId },
        volume: 0,
    } as unknown as BudgetModel;
}

/**
 * Helper to compare two ContractDate objects by month and year
 */
const isSameBudgetDate = (date1: ContractDate | undefined, date2: ContractDate | undefined): boolean => {
    if (!date1 || !date2) return false;
    return date1.month === date2.month && date1.year === date2.year;
};

// Flip card styles
const flipCardStyles = {
    container: {
        perspective: '1000px',
        cursor: 'pointer',
    },
    inner: (isFlipped: boolean) => ({
        position: 'relative' as const,
        width: '100%',
        height: '100%',
        transition: 'transform 0.6s',
        transformStyle: 'preserve-3d' as const,
        transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
    }),
    face: {
        position: 'absolute' as const,
        width: '100%',
        height: '100%',
        backfaceVisibility: 'hidden' as const,
        borderRadius: 16,
    },
    front: {
        backgroundColor: 'white',
    },
    back: {
        backgroundColor: 'white',
        transform: 'rotateY(180deg)',
    },
};

const SummaryCards: React.FC<SummaryProps> = ({
    futures,
    contractMonthMappings,
    positionsByAllocation,
    forecast,
    months,
    commodityId,
    deliveries = [],
    displayUnit
}) => {
    const [filteredPositions, setFilteredPositions] = useState<PositionModel[] | null>(null);
    const [flippedCards, setFlippedCards] = useState<Record<string, boolean>>({});
    const [showAllVolume, setShowAllVolume] = useState(false);

    // Four rows of cards put the grid's own horizontal scrollbar below the fold on a
    // small screen; this keeps one in view while the cards are, and only if they overflow.
    const cardsScrollRef = useRef<HTMLDivElement>(null);
    const floatingScrollbar = useFloatingHorizontalScrollbar(cardsScrollRef);

    // Use filteredPositions for totals if available, else positionsByAllocation
    const positionsForTotals = filteredPositions && filteredPositions.length > 0 ? filteredPositions : positionsByAllocation;

    const displayValue = (value: number | undefined | null) =>
        value === 0 || value === undefined || value === null ? '-' : value.toLocaleString();

    const displayStoneXNumber = (value: number | undefined | null, props: any = {}) =>
        value === 0 || value === undefined || value === null ? '-' : <StoneXNumber number={value} {...props} />;

    // Futures trade in a few contract months; budgets are monthly. Spread each
    // position over the budget months its contract month actually covers, so the
    // volume does not all land on the traded month.
    const processedFutures = splitFuturesAcrossBudgetMonths(futures, forecast, contractMonthMappings);

    // One card per fiscal month. Months the forecast does not cover get an empty
    // stand-in so the year reads as a full set, with dashes for the missing figures.
    const cards: BudgetModel[] = months && months.length > 0
        ? months.map(m => forecast.find(f => isSameBudgetDate(f.budgetDate, m)) ?? emptyBudgetForMonth(m, commodityId))
        : forecast;

    const cardKeyFor = (budget: BudgetModel) => `${budget.budgetDate?.year}-${budget.budgetDate?.month}`;

    const handleCardClick = (cardKey: string) => {
        setFlippedCards(prev => ({
            ...prev,
            [cardKey]: !prev[cardKey]
        }));
    };

    const handleFlipAll = () => {
        const newShowAll = !showAllVolume;
        setShowAllVolume(newShowAll);
        
        // Flip all cards to the same state
        const newFlippedCards: Record<string, boolean> = {};
        cards.forEach((card) => {
            newFlippedCards[cardKeyFor(card)] = newShowAll;
        });
        setFlippedCards(newFlippedCards);
    };

    // Helper to get delivery volume for a specific month.
    // Note: delivery quantities are already converted to the active display unit
    // by the parent Dashboard (see filteredAllocations memo). We just need to
    // sum them here so the value matches the forecast volume's unit.
    const getDeliveryVolumeForMonth = (
        budgetDate: ContractDate | undefined
    ): number => {
        if (!budgetDate || !deliveries.length) return 0;

        return deliveries
            .filter(d => {
                if (!d.documentDate) return false;
                const documentDate = new Date(d.documentDate);
                // Note: getMonth() returns 0-11, budgetDate.month is 1-12
                return (documentDate.getMonth() + 1) === budgetDate.month &&
                       documentDate.getFullYear() === budgetDate.year;
            })
            .reduce((sum, d) => sum + (d.quantity ?? 0), 0);
    };

    return (
        <Box>
            {/* Header with toggle button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2 }}>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleFlipAll();
                    }}
                    sx={{ textTransform: 'none' }}
                >
                    {showAllVolume ? 'Show Summary' : 'Show Volume'}
                </Button>
            </Box>

            {/* Cards grid */}
            {/* Scrollable outer container. Shows its own scrollbar only when the row of
                three does not fit; the floating bar below covers the case where that
                scrollbar is off the bottom of the screen. */}
            <Box
                ref={cardsScrollRef}
                sx={{
                    overflowX: 'auto',
                    width: '100%',
                    maxWidth: '100%',
                    minWidth: 0,
                    pb: 1,
                    '&::-webkit-scrollbar': {
                        height: 8,
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(0,0,0,0.2)',
                        borderRadius: 4,
                    },
                }}
            >
                {/* Inner grid with fixed minimum width */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: `repeat(${CARDS_PER_ROW}, 1fr)`,
                        gap: 2,
                        minWidth: CARDS_MIN_WIDTH,
                    }}
                >
                    {cards.map((budgets) => {
                    const cardKey = cardKeyFor(budgets);
                    const isFlipped = flippedCards[cardKey] ?? showAllVolume;
                    
                    const totalVolume = budgets.volume;
                    
                    // Use month/year comparison instead of label comparison for reliability
                    const longFuturesVolume = processedFutures
                        .filter(f => 
                            f.side === "Buy" && 
                            isSameBudgetDate(f.budgetDate, budgets.budgetDate) && 
                            f.commodity.commodityId === budgets.commodity.commodityId
                        )
                        .reduce((sum, p) => sum + ((p.volume ?? 0) * (p.unit === Unit.Contract ? p.positionPricingStrategy.contractVolume! : 1)), 0);
                    
                    const futuresVolume = processedFutures
                        .filter(f => 
                            isSameBudgetDate(f.budgetDate, budgets.budgetDate) && 
                            f.commodity.commodityId === budgets.commodity.commodityId
                        )
                        .reduce((sum, p) => sum + ((p.volume ?? 0) * (p.side === "Buy" ? 1 : -1) * (p.unit === Unit.Contract ? p.positionPricingStrategy.contractVolume! : 1)), 0);
                    
                    const longFuturesValue = processedFutures
                        .filter(f => 
                            f.side === "Buy" && 
                            isSameBudgetDate(f.budgetDate, budgets.budgetDate) && 
                            f.commodity.commodityId === budgets.commodity.commodityId
                        )
                        .reduce((sum, p) => sum + ((p.volume ?? 0) * (p.unit === Unit.Contract ? p.positionPricingStrategy.contractVolume! : 1) * (p.price ?? 0)), 0);
                    
                    const futuresValue = processedFutures
                        .filter(f => 
                            isSameBudgetDate(f.budgetDate, budgets.budgetDate) && 
                            f.commodity.commodityId === budgets.commodity.commodityId
                        )
                        .reduce((sum, p) => sum + ((p.volume ?? 0) * (p.side === "Buy" ? 1 : -1) * (p.unit === Unit.Contract ? p.positionPricingStrategy.contractVolume! : 1) * (p.price ?? 0)), 0);

                    const longFuturesWeightedAveragePrice = longFuturesVolume !== 0 ? longFuturesValue / longFuturesVolume : 0;
                    const assignedVolume = positionsByAllocation.filter(f => f.positionType != PositionType.Basis && f.budgetDate.label == budgets.budgetDate.label).reduce((sum, p) => sum + ((p.volume ?? 0) * (p.side === "Buy" ? 1 : -1)), 0);
                    const assignedValue = positionsByAllocation.filter(f => f.positionType != PositionType.Basis && f.budgetDate.label == budgets.budgetDate.label).reduce((sum, p) => sum + ((p.volume ?? 0) * (p.side === "Buy" ? 1 : -1) * (p.price ?? 0)), 0);
                    const assignedWeightedAveragePrice = assignedVolume !== 0 ? assignedValue / assignedVolume : 0;
                    const basisVolume = positionsByAllocation.filter(f => f.positionType == PositionType.Basis && f.budgetDate.label == budgets.budgetDate.label).reduce((sum, p) => sum + ((p.volume ?? 0) * (p.side === "Buy" ? 1 : -1)), 0);
                    const basisValue = positionsByAllocation.filter(f => f.positionType == PositionType.Basis && f.budgetDate.label == budgets.budgetDate.label).reduce((sum, p) => sum + ((p.volume ?? 0) * (p.price ?? 0)), 0);
                    const basisWeightedAveragePrice = basisVolume !== 0 ? basisValue / basisVolume : 0;
                    const totalHedgedVolume = futuresVolume + assignedVolume;
                    const totalHedgedValue = futuresValue + assignedValue + basisValue;
                    const totalHedgedWeightedAveragePrice = totalHedgedVolume !== 0 ? totalHedgedValue / totalHedgedVolume : 0;
                    const unassignedVolume = (budgets.volume ?? 0) - totalHedgedVolume;
                    const openVolume = unassignedVolume < 0 ? 0 : unassignedVolume;
                    const openValue = openVolume * (budgets.marketPrice ?? 0);
                        const totalWeightedAveragePrice = budgets.volume !== 0 ? (totalHedgedValue + openValue) / (totalHedgedVolume+openVolume) : 0;
                    const totalMTMValue = ((budgets.marketPrice ?? 0) - totalWeightedAveragePrice) * totalVolume;
                    const totalSpend = totalVolume * totalWeightedAveragePrice;

                    // Get delivery volume for bar chart (already in display unit
                    // because the parent converts deliveries.quantity up front)
                    const deliveryVolume = getDeliveryVolumeForMonth(budgets.budgetDate);

                    // Data for bar chart on back of card
                    const chartData = [
                        { name: 'Forecast', value: totalVolume ?? 0, fill: '#283593' },
                        { name: 'Assigned', value: assignedVolume ?? 0, fill: '#5c6bc0' },
                        { name: 'Delivered', value: deliveryVolume ?? 0, fill: '#9fa8da' },
                    ];

                    return (
                        <Box
                            key={cardKey}
                            sx={{
                                boxSizing: 'border-box',
                                mb: 4,
                                minHeight: 360,
                                ...flipCardStyles.container,
                            }}
                            onClick={() => handleCardClick(cardKey)}
                        >
                            <Box sx={flipCardStyles.inner(isFlipped)}>
                                {/* Front of card */}
                                <Box
                                    sx={{
                                        ...flipCardStyles.face,
                                        ...flipCardStyles.front,
                                    }}
                                >
                                    <Box
                                        className="position-card-summary"
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 1,
                                            flex: 1,
                                            p: 2,
                                            borderRadius: 4,
                                            height: '100%',
                                            boxSizing: 'border-box',
                                        }}>
                                        <Box
                                            sx={{
                                                backgroundColor: '#e3f2fd',
                                                borderRadius: 2,
                                                mb: 2,
                                                px: 2,
                                                py: 1,
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                            }}>
                                            <Typography
                                                variant="subtitle1"
                                                className="position-card-text"
                                                sx={{ flex: 1, textAlign: 'left', fontWeight: 'bold' }}
                                            >
                                                {budgets.budgetDate.label}
                                            </Typography>
                                            <Typography
                                                variant="subtitle1"
                                                className="position-card-text"
                                                sx={{ flex: 1, textAlign: 'right', fontWeight: 'bold' }}
                                            >
                                                {displayStoneXNumber(totalSpend, { currency: 'USD', decimals: 0 })}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                            <Typography variant="body2" className="position-card-text2" sx={{ flex: 2 }}>
                                                Current Forecast:
                                            </Typography>
                                            <Typography variant="body2" className="position-card-text2" sx={{ flex: 1, textAlign: 'right' }}>
                                                {displayStoneXNumber(totalVolume, { decimals: 0 })}
                                            </Typography>
                                            <Typography variant="body2" className="position-card-text2" sx={{ flex: 1, textAlign: 'right' }}>
                                            </Typography>
                                            <Typography variant="body2" className="position-card-text2" sx={{ flex: 1, textAlign: 'right' }}>
                                                {displayStoneXNumber(budgets.price, { currency: 'USD', decimals: 2 })}
                                            </Typography>
                                        </Box>

                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                            <Typography sx={{ flex: 2 }}>Long Futures:</Typography>
                                            <Typography sx={{ flex: 1, textAlign: 'right' }}>
                                                {displayStoneXNumber(longFuturesVolume, {  decimals: 0 })}
                                            </Typography>
                                            <Typography sx={{ flex: 1, textAlign: 'right' }}>
                                                {displayStoneXNumber(totalVolume == 0 ? 0 : (longFuturesVolume / totalVolume), { percent: true, decimals: 0 })}
                                            </Typography>
                                            <Typography sx={{ flex: 1, textAlign: 'right' }}>
                                                {displayStoneXNumber(longFuturesWeightedAveragePrice, { currency: 'USD', decimals: 2 })}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                            <Typography variant="body2" className="position-card-text2" sx={{ flex: 2 }}>
                                                Assigned Positions:
                                            </Typography>
                                            <Typography variant="body2" className="position-card-text2" sx={{ flex: 1, textAlign: 'right' }}>
                                                {displayStoneXNumber(assignedVolume, { decimals: 0 })}
                                            </Typography>
                                            <Typography variant="body2" className="position-card-text2" sx={{ flex: 1, textAlign: 'right' }}>
                                                {displayStoneXNumber(totalVolume == 0 ? 0 : (assignedVolume / totalVolume), { percent: true, decimals: 0 })}
                                            </Typography>
                                            <Typography variant="body2" className="position-card-text2" sx={{ flex: 1, textAlign: 'right' }}>
                                                {displayStoneXNumber(assignedWeightedAveragePrice, { currency: 'USD', decimals: 2 })}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                            <Typography variant="body2" className="position-card-text2" sx={{ flex: 2 }}>
                                                Basis Positions:
                                            </Typography>
                                            <Typography variant="body2" className="position-card-text2" sx={{ flex: 1, textAlign: 'right' }}>
                                                {displayValue(basisVolume)}
                                            </Typography>
                                            <Typography variant="body2" className="position-card-text2" sx={{ flex: 1, textAlign: 'right' }}>
                                                {displayStoneXNumber(totalVolume == 0 ? 0 : (basisVolume / totalVolume), { percent: true, decimals: 0 })}
                                            </Typography>
                                            <Typography variant="body2" className="position-card-text2" sx={{ flex: 1, textAlign: 'right' }}>
                                                {displayStoneXNumber(basisWeightedAveragePrice, { currency: 'USD', decimals: 2 })}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                            <Typography variant="body2" className="position-card-text2" sx={{ flex: 2 }}>
                                                Market Price:
                                            </Typography>
                                            <Typography variant="body2" className="position-card-text2" sx={{ flex: 1 }} />
                                            <Typography variant="body2" className="position-card-text2" sx={{ flex: 1 }} />
                                            <Typography variant="body2" className="position-card-text2" sx={{ flex: 1, textAlign: 'right' }}>
                                                {displayStoneXNumber(budgets.marketPrice, { currency: 'USD', decimals: 2 })}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                            <Typography variant="body2" className="position-card-text2">
                                                Weighted Price:
                                            </Typography>
                                            <Typography variant="body2" className="position-card-text2">
                                                {displayStoneXNumber(totalWeightedAveragePrice, { currency: 'USD', decimals: 2 })}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                            <Typography variant="body2" className="position-card-text2">
                                                MTM Value:
                                            </Typography>
                                            <Typography variant="body2" className="position-card-text2">
                                                {displayStoneXNumber(totalMTMValue, { currency: 'USD', decimals: 0, colored: true })}
                                            </Typography>
                                        </Box>
                                        <Typography variant="caption" sx={{ mt: 'auto', textAlign: 'center', color: 'text.secondary' }}>
                                            Click to see volume breakdown
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Back of card */}
                                <Box
                                    sx={{
                                        ...flipCardStyles.face,
                                        ...flipCardStyles.back,
                                    }}
                                >
                                    <Box
                                        className="position-card-summary"
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 1,
                                            flex: 1,
                                            p: 2,
                                            borderRadius: 4,
                                            height: '100%',
                                            boxSizing: 'border-box',
                                        }}>
                                        <Box
                                            sx={{
                                                backgroundColor: '#e3f2fd',
                                                borderRadius: 2,
                                                mb: 1,
                                                px: 2,
                                                py: 1,
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                            }}>
                                            <Typography
                                                variant="subtitle1"
                                                className="position-card-text"
                                                sx={{ fontWeight: 'bold' }}
                                            >
                                                {budgets.budgetDate.label} - Volume Breakdown {displayUnit ? `(${displayUnit})` : ''}
                                            </Typography>
                                        </Box>
                                        
                                        <Box sx={{ flex: 1, width: '100%', minHeight: 300 }}>
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart
                                                    data={chartData}
                                                    margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                                                >
                                                    <CartesianGrid strokeDasharray="3 3" />
                                                    <XAxis 
                                                        dataKey="name" 
                                                        angle={-45}
                                                        textAnchor="end"
                                                        interval={0}
                                                        height={60}
                                                        tick={{ fontSize: 12 }}
                                                    />
                                                    <YAxis tickFormatter={(value: number) => value.toLocaleString()} />
                                                    <Tooltip 
                                                        formatter={(value) => 
                                                            typeof value === 'number' 
                                                                ? value.toLocaleString(undefined, { maximumFractionDigits: 0 }) 
                                                                : String(value ?? '')
                                                        }
                                                        labelFormatter={(label) => label}
                                                    />
                                                    <Bar dataKey="value" fill="#283593">
                                                        {chartData.map((entry, index) => (
                                                            <Cell key={`cell-${index}`} fill={entry.fill} />
                                                        ))}
                                                    </Bar>
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </Box>

                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    );
                })}
                </Box>
            </Box>

            {floatingScrollbar}
        </Box>
    );
};

export default SummaryCards;